import type { AdditionalRanking, EduRanking } from 'interfaces/EduRanking';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import RankingCard, { type RankingCardProps } from '../RankingCard';
import * as S from './RankingCardListStyles';

export type Variant =
  | 'education-section-rankings'
  | 'best-programs-category-page'
  | 'rankings-list-category-page'
  | 'more-best-rankings';

const getCardListProps = (sectionVariant: Variant, index?: number): RankingCardProps['variant'] => {
  switch (sectionVariant) {
    case 'education-section-rankings':
      return index === 0 ? 'card-hero' : 'card-row';
    case 'best-programs-category-page':
      return 'card-column';
    case 'rankings-list-category-page':
      return 'card-noimage';
    case 'more-best-rankings':
      return 'card-link';
    default:
      return 'card-column';
  }
};

interface RankingCardListProps {
  rankings: (EduRanking | AdditionalRanking)[] | EduRanking['childRankings'];
  variant: Variant;
  children?: React.ReactNode;
}

const RankingCardList = ({ rankings, variant, children }: RankingCardListProps) => {
  if (!rankings || rankings.length === 0) return null;

  return (
    <S.CardsWrapper $variant={variant}>
      <>
        {rankings.map((ranking, index) => {
          const { image, titleWithYear, title, description, url } = ranking as EduRanking;
          return (
            <RankingCard
              key={url}
              image={image}
              title={titleWithYear || title}
              description={description}
              url={url}
              variant={getCardListProps(variant, index)}
              cta={
                variant === 'rankings-list-category-page'
                  ? {
                      title: 'SEE FULL RANKING',
                      url,
                    }
                  : undefined
              }
            />
          );
        })}
        {children}
      </>
    </S.CardsWrapper>
  );
};

export default withErrorBoundary(RankingCardList, { fragment: UIFragments.EDU_RANKING_CARDS_LIST });
